/**
 * Худоба: свині й тури.
 *
 * The first things in this world that are alive and are not trying to kill
 * you. Everything before them either stood still (the villagers) or walked
 * straight at you (the orcs); these wander about minding their own business
 * until something happens to them, and then they run.
 *
 * **Neither of them fights back.** A тур is drawn as the wild bull this
 * country had before anyone fenced anything, horns and all, and an animal
 * that size turning round and goring you was the first thing written here -
 * but the ask was for the behaviour every player already knows from
 * Minecraft, and there a cow is a thing you walk up to. So the horns are a
 * portrait and not a threat, and both species do exactly one thing when hit:
 * bolt, for a few seconds, in a straight line away.
 *
 * Pure, like the orcs: positions, a clock and a mover. "Does a frightened pig
 * actually get away from you" is a question the test suite answers.
 */

import { moveAABB, type SolidTest } from "../physics/voxelCollision.js";
import { Vitals } from "./vitals.js";
import { PlainItemId } from "../block/items.js";

export type BeastKind = "pig" | "tur" | "kurka" | "sobaka" | "kit" | "zmii";

/**
 * What an animal is for.
 *
 * `prey` is everything that was here first: it grazes, it bolts, and the
 * only thing it knows about a person is which way to run. `companion` is the
 * собака and the кіт - they come *towards* a person instead of away, which
 * is one line of arithmetic and an entirely different animal.
 */
export type BeastTemper = "prey" | "companion";

export interface BeastBreed {
  readonly kind: BeastKind;
  readonly label: string;
  /** The same name in English. */
  readonly en: string;
  readonly maxHp: number;
  readonly width: number;
  readonly height: number;
  /** Blocks a second while grazing. */
  readonly walk: number;
  /** Blocks a second while running from something. */
  readonly bolt: number;
  /** How high it steps without jumping. */
  readonly step: number;
  /**
   * What killing one yields: one to three, the way it is where this comes from.
   *
   * Absent on the ones that yield nothing. It used to be written as
   * `min: 0, max: 0` on the кіт and the собака, and that rolled a stack of
   * *no* сало which landed on the ground as a pickup nobody could pick up:
   * "м'ясо яке падає з котів або собак не береться в інвентар". An animal
   * that yields nothing says so by having no `drop`, not by having a drop of
   * nothing.
   */
  readonly drop?: { readonly item: number; readonly min: number; readonly max: number };
  /**
   * And a кістка, off anything that had one.
   *
   * Its own field rather than a second entry in `drop`, because `drop` is
   * "what this animal is *for*" - the сало, the яловичина - and a bone is
   * what is left of any of them. A кіт and a собака have none: there is
   * nothing to do to a cat, and a dog you fed is not a thing to render down.
   */
  readonly bones?: number;
  readonly temper: BeastTemper;
  /**
   * How close a companion likes to be, in blocks.
   *
   * A dog walks at your heel and a cat keeps its distance, and that number
   * is most of what makes them read as a dog and a cat rather than as two
   * animals with different fur.
   */
  readonly keep?: number;
  /** What a bite takes off, for the ones that bite at all. */
  readonly bite?: number;
  /** Can this one be won over with food? */
  readonly tameable?: boolean;
}

/** The pig from the photograph: long, heavy, low to the ground. */
export const PIG: BeastBreed = {
  kind: "pig", label: "свиня", en: "pig",
  maxHp: 10, width: 0.9, height: 0.9,
  walk: 1.5, bolt: 4.2, step: 0.6,
  drop: { item: PlainItemId.RawPork, min: 1, max: 3 },
  bones: 2,
  temper: "prey",
};

/**
 * The тур: taller and broader, and worth more meat for it.
 *
 * The same ten points of сила as the pig, because that is what a cow has
 * where this behaviour comes from. It is bigger to look at and slower to
 * wander, and that is the whole of the difference.
 */
export const TUR: BeastBreed = {
  kind: "tur", label: "тур", en: "aurochs",
  maxHp: 10, width: 1.3, height: 1.7,
  walk: 1.2, bolt: 3.6, step: 0.6,
  drop: { item: PlainItemId.RawBeef, min: 1, max: 3 },
  bones: 3,
  temper: "prey",
};

/**
 * Курка: полтавська глиняста, with a чуб off the українська чубата.
 *
 * Both are Ukrainian breeds and neither looks anything like the white bird
 * every other game draws. The полтавська is a clay-coloured hen - that is
 * what «глиняста» means - light brown through the body with black on the
 * tips of the flight and tail feathers, a yellow beak and yellow legs. The
 * чубата's one distinguishing feature is the tuft on its head, which carries
 * no purpose whatever beyond looking like that, and which tips the comb over
 * to one side.
 *
 * Small, quick and not worth much: four points of сила against the pig's
 * ten, one piece of meat to three, and it bolts faster than either of the
 * others. A herd of them is the noise of a подвір'я rather than a larder.
 */
export const KURKA: BeastBreed = {
  kind: "kurka", label: "курка", en: "hen",
  maxHp: 4, width: 0.5, height: 0.7,
  walk: 1.4, bolt: 4.6, step: 0.6,
  drop: { item: PlainItemId.RawChicken, min: 1, max: 2 },
  bones: 1,
  temper: "prey",
};

/**
 * Собака: the yard dog, not the wolf.
 *
 * Ukrainian yard dogs are middling, rough-coated and sandy, and the
 * дворняга is what actually lies by a хата - so this is a medium dog the
 * colour of straw rather than any breed in particular.
 *
 * Tameable with meat and nothing else, and once it is yours it walks at your
 * heel and goes for whatever goes for you. Four points of a bite is under
 * half a sabre: a dog is help in a fight, not a way to win one without
 * turning up.
 */
export const SOBAKA: BeastBreed = {
  kind: "sobaka", label: "собака", en: "dog",
  maxHp: 12, width: 0.6, height: 0.8,
  walk: 2.0, bolt: 5.4, step: 0.6,
  temper: "companion", keep: 2.5, bite: 4, tameable: true,
};

/**
 * Кіт: about the place, on its own business.
 *
 * Comes near people and will not be tamed, which is the joke and also the
 * design: there is nothing to do to a cat. It keeps six blocks off, it does
 * not fight, and killing one yields nothing at all - there is no use for a
 * dead cat in this game and there is not going to be one.
 */
export const KIT: BeastBreed = {
  kind: "kit", label: "кіт", en: "cat",
  maxHp: 6, width: 0.4, height: 0.5,
  walk: 1.8, bolt: 5.0, step: 0.6,
  temper: "companion", keep: 6,
};

/**
 * Змій Горинич, out of the character sheet.
 *
 * A thousand points of health against a тур's ten, and four blocks tall at
 * the shoulder against its one and seven tenths - the sheet puts him at six
 * to eight metres against a man's one point eight, and this is that.
 *
 * `temper: "prey"` and it is a lie, and a deliberate one for now. Nothing in
 * this file can be hostile: a beast grazes, bolts, or follows somebody home,
 * and that is the whole of what a beast does. A Змій that attacks needs
 * machinery the orcs have and the animals do not, and it is not here yet -
 * so what exists today is his body, which is the part worth looking at
 * first, and nothing spawns him. See the commit.
 */
export const ZMII: BeastBreed = {
  kind: "zmii", label: "Змій Горинич", en: "Zmii Horynych",
  maxHp: 1000, width: 2.6, height: 4,
  walk: 1.4, bolt: 4.5, step: 1.2,
  /*
   * Twenty-three scales, which is a set of armour and not a quarter of one.
   *
   * "З одного дракона випадає достатньо на сет обладунків." The four
   * patterns want six, eight, four and five, and that is twenty-three - the
   * number is the recipes added up rather than a round figure near them. I
   * wrote twenty-four from memory and the check that adds the patterns up
   * said 23, which is exactly what it is for: change a pattern and the drop
   * has to change with it, or a dragon quietly stops being a full suit.
   */
  drop: { item: PlainItemId.DragonScale, min: 23, max: 23 },
  temper: "prey",
};

export const BREEDS: Readonly<Record<BeastKind, BeastBreed>> =
  { pig: PIG, tur: TUR, kurka: KURKA, sobaka: SOBAKA, kit: KIT, zmii: ZMII };

/** How long a startled animal keeps running. */
export const SPOOKED_SECONDS = 6;
/** How long one leg of a wander lasts before it picks another heading. */
const WANDER_SECONDS = 4;
/** Share of wander legs spent standing still rather than walking. */
const IDLE_SHARE = 0.4;

/**
 * How far down an animal will willingly step.
 *
 * One block. Without this a wandering herd walks off the island: the sea is
 * an absorbing boundary and a random walk on a finite island always ends in
 * it - measured at nine of fourteen animals in the water inside one minute,
 * and thirteen of fourteen inside five.
 */
const MAX_DROP = 1;

const GRAVITY = 32;
const TERMINAL = 48;
const DAMPING = 8;
const JUMP = 8.4;

export interface Beast {
  readonly id: number;
  readonly breed: BeastBreed;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  yaw: number;
  onGround: boolean;
  readonly vitals: Vitals;
  /** Seconds left of running away. */
  spooked: number;
  /** What it is running from. */
  threatX: number;
  threatZ: number;
  /** Seconds left of the current wander leg. */
  leg: number;
  /** Heading for this leg, and whether it is walking at all. */
  legYaw: number;
  legMoving: boolean;
  /**
   * Whose dog it is, or null for one that belongs to nobody yet.
   *
   * A number rather than a reference, because online the person it follows
   * is a client id on a server and there is no object here to point at.
   */
  owner: number | null;
  /** Seconds until it can bite again. */
  rest: number;
}

let nextId = 1;

export function makeBeast(
  kind: BeastKind, at: { x: number; y: number; z: number },
): Beast {
  const breed = BREEDS[kind];
  return {
    id: nextId++,
    breed,
    x: at.x, y: at.y, z: at.z,
    vx: 0, vy: 0, vz: 0,
    yaw: 0,
    onGround: false,
    vitals: new Vitals(breed.maxHp),
    spooked: 0,
    threatX: 0,
    threatZ: 0,
    leg: 0,
    legYaw: 0,
    legMoving: false,
    owner: null,
    rest: 0,
  };
}


/**
 * One the server owns, drawn here.
 *
 * Built rather than made-and-renumbered, for the reason `mirrorOrc` is: the
 * makers take the next local id as they go, so mirroring a herd would push
 * this world's own numbering along for bodies it never made.
 */
export function mirrorBeast(
  id: number, kind: BeastKind, at: { x: number; y: number; z: number },
): Beast {
  const breed = BREEDS[kind];
  return {
    id, breed,
    x: at.x, y: at.y, z: at.z,
    vx: 0, vy: 0, vz: 0,
    yaw: 0,
    onGround: true,
    vitals: new Vitals(breed.maxHp),
    spooked: 0, threatX: 0, threatZ: 0,
    leg: 0, legYaw: 0, legMoving: false,
    owner: null, rest: 0,
  };
}

/**
 * Is there anywhere to put a foot that way?
 *
 * Refuses two things and only two: water, and a drop of more than MAX_DROP.
 * An animal that will not enter water and will not walk off a ledge stays on
 * its island without needing to know that it is on one - which is the whole
 * trick, because it has no map and no path-finding and is never getting
 * either.
 */
function footing(
  isSolid: SolidTest,
  isFluid: SolidTest,
  breed: BeastBreed,
  x: number, y: number, z: number,
  dirX: number, dirZ: number,
): boolean {
  const ax = Math.floor(x + dirX * (breed.width / 2 + 0.6));
  const az = Math.floor(z + dirZ * (breed.width / 2 + 0.6));
  const feet = Math.floor(y);

  // Water where it would stand, or under it. Both, because a shore shelves:
  // the cell at foot level is air right up until the step that puts the
  // animal in the sea, and by then it is already swimming.
  if (isFluid(ax, feet, az) || isFluid(ax, feet - 1, az)) return false;

  // A block to step up onto is not a drop.
  if (isSolid(ax, feet, az)) return true;

  for (let d = 1; d <= MAX_DROP + 1; d++) {
    if (isSolid(ax, feet - d, az)) return d <= MAX_DROP + 1;
  }
  return false;
}

/**
 * Tell an animal something just happened to it, and where from.
 *
 * Separate from `Vitals.hurt`: an arrow out of the dark and a sabre in the
 * ribs do the same damage and mean different things about which way to run,
 * and only the caller knows which end it came from.
 */
export function spook(beast: Beast, fromX: number, fromZ: number): void {
  beast.spooked = SPOOKED_SECONDS;
  beast.threatX = fromX;
  beast.threatZ = fromZ;
}

/**
 * Roll what one yields when it goes down.
 *
 * Random and not seeded, like the block drops: what falls out of an animal is
 * not world state and need not survive a reload.
 */
export function rollBeastDrop(
  beast: Beast, random: () => number = Math.random,
): { item: number; count: number }[] {
  const meat = beast.breed.drop;
  const out = meat === undefined ? [] :
    [{ item: meat.item, count: meat.min + Math.floor(random() * (meat.max - meat.min + 1)) }];
  // And the bones, which are certain: an animal either has them or is a cat.
  const bones = beast.breed.bones ?? 0;
  if (bones > 0) out.push({ item: PlainItemId.Bone, count: bones });
  return out;
}

/**
 * Somebody a companion can walk beside, and what is currently after them.
 *
 * The foe is passed in rather than looked up because nothing in this file
 * knows what an орк is, and it should stay that way: a dog goes for whatever
 * its person is fighting, and who that is happens to be a question only the
 * game loop can answer.
 */
export interface Company {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /**
   * What is currently on them, if anything.
   *
   * Carries an id as well as a place, because a bite has to land on a body
   * and this file has no idea what kind of body it is.
   */
  readonly foe?: {
    readonly id: number;
    readonly x: number; readonly y: number; readonly z: number;
  } | null;
}

/** A dog got its teeth into something. */
export interface Bite {
  readonly beast: Beast;
  /** Whatever the person it belongs to was fighting. */
  readonly foe: number;
  readonly damage: number;
}

export interface BeastTurn {
  /** The ones that went down this tick. */
  readonly fallen: Beast[];
  /** And the ones that got their teeth in. */
  readonly bites: Bite[];
}

/**
 * Which way a beast is walking, for a given yaw.
 *
 * The one place that knows. A yaw of nought points along -Z here - the same
 * convention the player, the orcs and the arrows use - so the heading is
 * `(-sin, -cos)` and not `(sin, cos)`, and getting that backwards puts an
 * animal's tail where its head should be. Which is exactly what happened to
 * the собака and the кіт: they walked to you perfectly and arrived facing
 * away, and nobody could see either of their faces.
 */
export function headingOf(yaw: number): { x: number; z: number } {
  return { x: -Math.sin(yaw), z: -Math.cos(yaw) };
}

/** And the yaw that walks that way. The inverse of `headingOf`. */
export function yawToward(x: number, z: number): number {
  return Math.atan2(-x, -z);
}

/** How near a dog has to be to bite, and how long between bites. */
const BITE_REACH = 1.6;
const BITE_REST = 1.1;
/**
 * How far a companion will follow before it gives up and goes back to
 * wandering. Further than a person can get in a moment, nearer than a walk
 * across the country: a dog left behind at the far end of a map and pulled
 * towards you forever is a dog running through hillsides.
 */
const COMPANY_REACH = 40;

/**
 * How near something hostile has to be to count as being *on* somebody.
 *
 * The rule a dog works by, and it is deliberately the simplest one there is:
 * whatever is nearest your person inside this. No memory of who started it,
 * no list of grudges - a dog that goes for the orc standing over you is a
 * dog, and a dog that remembers an orc from four minutes ago is a diary.
 */
export const GUARD_REACH = 8;

/**
 * Advance every animal.
 *
 * @returns what went down this tick, so the caller can leave the meat where
 * they fell, and what bit something, so the caller can take the damage off
 * whatever it was. Reported from here rather than at each place a blow
 * lands, because a sabre, an arrow and anything added later all end the same
 * way and only one of them should have to know what a pig is worth.
 */
export function stepBeasts(
  beasts: Beast[],
  dt: number,
  isSolid: SolidTest,
  isFluid: SolidTest,
  random: () => number = Math.random,
  company: readonly Company[] = [],
): BeastTurn {
  const fallen: Beast[] = [];
  const bites: Bite[] = [];

  for (let i = beasts.length - 1; i >= 0; i--) {
    const beast = beasts[i]!;
    const breed = beast.breed;
    beast.vitals.step(dt);
    if (beast.vitals.dead) {
      beasts.splice(i, 1);
      fallen.push(beast);
      continue;
    }
    if (beast.spooked > 0) beast.spooked = Math.max(0, beast.spooked - dt);
    if (beast.rest > 0) beast.rest = Math.max(0, beast.rest - dt);

    let wantX = 0;
    let wantZ = 0;
    let speed = breed.walk;

    if (beast.spooked > 0) {
      // Straight away from whatever it was, and it does not look back.
      const dx = beast.x - beast.threatX;
      const dz = beast.z - beast.threatZ;
      const flat = Math.hypot(dx, dz);
      if (flat > 0.001) {
        beast.yaw = yawToward(dx, dz);
        wantX = dx / flat;
        wantZ = dz / flat;
        speed = breed.bolt;
      }
    } else if (breed.temper === "companion" &&
      /*
       * A dog you have not fed is not your dog.
       *
       * It wanders like everything else until somebody gives it meat, and
       * that is the whole of what taming *looks* like from outside - without
       * it, every stray in the country walked at your heel and feeding one
       * changed nothing anybody could see. A кіт is never tamed and comes
       * near anyway, which is the joke and also the design.
       */
      !(breed.tameable === true && beast.owner === null) &&
      (beast.owner === null || company.some((p) => p.id === beast.owner))) {
      /*
       * Towards somebody, instead of away from them.
       *
       * A tamed dog follows the person it belongs to and nobody else; a
       * stray dog and every cat drift towards whoever is nearest. What each
       * of them does when it gets there is the `keep` distance and nothing
       * else: two and a half blocks is at your heel, six is a cat sitting on
       * the other side of the yard watching you work.
       */
      let with_: Company | null = null;
      let best = COMPANY_REACH;
      for (const person of company) {
        if (beast.owner !== null && person.id !== beast.owner) continue;
        const away = Math.hypot(person.x - beast.x, person.z - beast.z);
        if (away < best) { best = away; with_ = person; }
      }

      /*
       * Whatever is on them, if this dog is theirs and has teeth.
       *
       * A stray will not fight for you and a cat will not fight at all,
       * which is the whole difference between the two animals as far as
       * anything outside this file is concerned.
       */
      const foe = with_ !== null && beast.owner !== null && breed.bite !== undefined
        ? with_.foe ?? null : null;
      const goal = foe ?? with_;

      if (goal !== null) {
        const dx = goal.x - beast.x;
        const dz = goal.z - beast.z;
        const flat = Math.hypot(dx, dz);
        const near = foe !== null ? BITE_REACH : breed.keep ?? 3;
        if (flat > 0.001) beast.yaw = yawToward(dx, dz);
        if (flat > near) {
          wantX = dx / flat;
          wantZ = dz / flat;
          // Trotting only when it has fallen behind, or a dog at your heel
          // spends the whole game sprinting into the back of your legs.
          speed = flat > near * 3 ? breed.bolt : breed.walk;
        }
        if (foe !== null && flat <= BITE_REACH && beast.rest <= 0 &&
          Math.abs(foe.y - beast.y) <= 2) {
          beast.rest = BITE_REST;
          bites.push({ beast, foe: foe.id, damage: breed.bite ?? 0 });
        }
      } else {
        // Nobody about: wander like anything else, so a dog left behind is a
        // dog in a field rather than a statue.
        beast.leg -= dt;
        if (beast.leg <= 0) {
          beast.leg = WANDER_SECONDS * (0.5 + random());
          beast.legMoving = random() > IDLE_SHARE;
          beast.legYaw = random() * Math.PI * 2;
        }
        if (beast.legMoving) {
          beast.yaw = beast.legYaw;
          const step = headingOf(beast.legYaw);
          wantX = step.x;
          wantZ = step.z;
        }
      }
    } else {
      // Grazing. A new heading every few seconds, and some of those legs are
      // spent standing still - a field of animals all permanently in motion
      // reads as a machine rather than as a herd.
      beast.leg -= dt;
      if (beast.leg <= 0) {
        beast.leg = WANDER_SECONDS * (0.5 + random());
        beast.legMoving = random() > IDLE_SHARE;
        beast.legYaw = random() * Math.PI * 2;
      }
      if (beast.legMoving) {
        beast.yaw = beast.legYaw;
        const step = headingOf(beast.legYaw);
        wantX = step.x;
        wantZ = step.z;
      }
    }

    // Nowhere to put a foot that way: stop, and if it was grazing, end the
    // leg so a new heading gets picked on the next tick rather than in four
    // seconds' time. A frightened animal stops at the edge too - drowning to
    // get away from a sabre is not an improvement.
    if ((wantX !== 0 || wantZ !== 0) && !footing(
      isSolid, isFluid, breed, beast.x, beast.y, beast.z, wantX, wantZ,
    )) {
      wantX = 0;
      wantZ = 0;
      beast.leg = 0;
    }

    if (wantX !== 0 || wantZ !== 0) {
      beast.vx = wantX * speed;
      beast.vz = wantZ * speed;
      // Something in the way and solid ground underfoot: hop it. Same rule as
      // the orcs, and the same consequence - a fence two blocks high holds,
      // which is what makes penning them possible at all.
      if (beast.onGround) {
        const aheadX = beast.x + wantX * (breed.width / 2 + 0.35);
        const aheadZ = beast.z + wantZ * (breed.width / 2 + 0.35);
        if (isSolid(Math.floor(aheadX), Math.floor(beast.y + 0.2), Math.floor(aheadZ))) {
          beast.vy = JUMP;
          beast.onGround = false;
        }
      }
    } else {
      beast.vx -= beast.vx * Math.min(1, DAMPING * dt);
      beast.vz -= beast.vz * Math.min(1, DAMPING * dt);
    }

    beast.vy = Math.max(-TERMINAL, beast.vy - GRAVITY * dt);

    const moved = moveAABB(
      beast.x, beast.y, beast.z,
      breed.width / 2, breed.height,
      beast.vx * dt, beast.vy * dt, beast.vz * dt,
      isSolid,
      breed.step,
      // Same rule as the player: a step needs a foot on the floor.
      beast.onGround,
    );
    beast.x = moved.x;
    beast.y = moved.y;
    beast.z = moved.z;
    beast.onGround = moved.onGround;
    if (moved.onGround && beast.vy < 0) beast.vy = 0;
    if (moved.hitCeiling && beast.vy > 0) beast.vy = 0;
  }

  return { fallen, bites };
}
